/**
 * Vías Module UI Store — Zustand
 *
 * @module core/modules/vias/store
 *
 * @description
 * Estado de la UI específico del módulo Vías.
 * Gestiona selección de elementos, filtros y vista del panel derecho.
 * Los modos operacionales son gestionados por el store global.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { eventBus } from '../../../event-bus';
import type {
  RoadType,
  SurfaceType,
  SegmentStatus,
  ConditionPriority,
  ViasRightView,
} from '../types';

// ============================================================================
// TYPES
// ============================================================================

/** Tipo del elemento seleccionado en el módulo */
export type ViasSelectionType =
  | 'none'
  | 'segment'
  | 'intersection'
  | 'sign'
  | 'bike_lane'
  | 'condition'
  | 'project';

export interface ViasSelection {
  type: ViasSelectionType;
  id: string | null;
  data: unknown | null;
}

export interface ViasUIState {
  /** Selección actual */
  selection: ViasSelection;

  /** Vista del panel derecho @default 'summary' */
  rightPanelView: ViasRightView;

  /* ===== FILTERS ===== */
  roadTypeFilter: RoadType[];
  surfaceTypeFilter: SurfaceType[];
  segmentStatusFilter: SegmentStatus[];
  conditionPriorityFilter: ConditionPriority[];
}

export interface ViasUIActions {
  selectElement: (type: ViasSelectionType, id: string, data?: unknown) => void;
  clearSelection: () => void;
  setRightPanelView: (view: ViasRightView) => void;

  /* ===== FILTERS ===== */
  setRoadTypeFilter: (types: RoadType[]) => void;
  setSurfaceTypeFilter: (types: SurfaceType[]) => void;
  setSegmentStatusFilter: (statuses: SegmentStatus[]) => void;
  setConditionPriorityFilter: (priorities: ConditionPriority[]) => void;
  clearFilters: () => void;

  reset: () => void;
}

export type ViasUIStore = ViasUIState & ViasUIActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ViasUIState = {
  selection: { type: 'none', id: null, data: null },
  rightPanelView: 'summary',
  roadTypeFilter: [],
  surfaceTypeFilter: [],
  segmentStatusFilter: [],
  conditionPriorityFilter: [],
};

// ============================================================================
// STORE
// ============================================================================

export const useViasUIStore = create<ViasUIStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      selectElement: (type: ViasSelectionType, id: string, data?: unknown): void => {
        const viewMap: Record<ViasSelectionType, ViasRightView> = {
          none:         'summary',
          segment:      'segmentDetails',
          intersection: 'intersectionDetails',
          sign:         'signDetails',
          bike_lane:    'bikeLaneDetails',
          condition:    'conditionDetails',
          project:      'projectDetails',
        };

        set({
          selection: { type, id, data },
          rightPanelView: viewMap[type],
        });

        eventBus.emit('vias.selection.changed', { type, id, timestamp: Date.now() });
        console.debug(`[ViasUI] Selected ${type}: ${id}`);
      },

      clearSelection: (): void => {
        const previous = get().selection;
        if (previous.type === 'none') return;

        set({ selection: { type: 'none', id: null, data: null }, rightPanelView: 'summary' });

        eventBus.emit('vias.selection.changed', { type: 'none', timestamp: Date.now() });
        console.debug('[ViasUI] Selection cleared');
      },

      setRightPanelView: (view: ViasRightView): void => {
        const prev = get().rightPanelView;
        if (prev === view) return;

        set({ rightPanelView: view });

        eventBus.emit('vias.view.changed', { view, previousView: prev, timestamp: Date.now() });
        console.debug(`[ViasUI] Panel view: ${prev} → ${view}`);
      },

      // Filters
      setRoadTypeFilter: (types) => set({ roadTypeFilter: types }),
      setSurfaceTypeFilter: (types) => set({ surfaceTypeFilter: types }),
      setSegmentStatusFilter: (statuses) => set({ segmentStatusFilter: statuses }),
      setConditionPriorityFilter: (priorities) => set({ conditionPriorityFilter: priorities }),
      clearFilters: () => set({
        roadTypeFilter: [],
        surfaceTypeFilter: [],
        segmentStatusFilter: [],
        conditionPriorityFilter: [],
      }),

      reset: (): void => {
        set(initialState);
        console.debug('[ViasUI] Reset to initial state');
      },
    }),
    { name: 'vias-ui-store' }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

export const selectViasSelection    = (s: ViasUIStore): ViasSelection  => s.selection;
export const selectViasHasSelection = (s: ViasUIStore): boolean        => s.selection.type !== 'none';
export const selectViasRightView    = (s: ViasUIStore): ViasRightView  => s.rightPanelView;
